// ============================================================================
// Dashboard card (D4) — surfaces incentive periods needing action with a
// link into the module. Self-fetching + RLS-scoped, so it shows the viewer's
// own actionable queue. Renders an "all clear" line when nothing is pending.
// ============================================================================

import { Link } from 'react-router-dom';
import { BadgeDollarSign } from 'lucide-react';
import { useIncentiveQueue } from './hooks/use-incentive-data';
import { formatPeriod, payoutTypeShort } from './format';

export function IncentiveQueueWidget() {
  const { rows, isLoading } = useIncentiveQueue();
  const actionable = rows.filter((r) => r.queue_status !== 'complete');
  const pendingTotal = rows.reduce((s, r) => s + r.pending_review_count, 0);

  return (
    <div className="rounded-xl border border-tcs-border bg-tcs-bg p-5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <BadgeDollarSign className="h-4 w-4 text-tcs-text-secondary" />
          <h3 className="text-sm font-semibold text-tcs-text">
            Performance payments
          </h3>
        </div>
        {/* CW-N 1c: point at the canonical path, not the legacy /incentive
            prefix that only works because of a redirect route. */}
        <Link
          to="/money/incentives"
          className="text-xs font-medium text-tcs-text underline-offset-2 hover:underline"
        >
          Open
        </Link>
      </div>

      {isLoading ? (
        <p className="mt-3 text-sm text-tcs-text-muted">Loading…</p>
      ) : actionable.length === 0 ? (
        <p className="mt-3 text-sm text-tcs-text-muted">
          Nothing needs review right now.
        </p>
      ) : (
        <div className="mt-3">
          <p className="text-sm text-tcs-text">
            <span className="font-semibold">{actionable.length}</span> period
            {actionable.length === 1 ? '' : 's'} need action
            {pendingTotal > 0 && (
              <>
                {' '}·{' '}
                <span className="font-semibold">{pendingTotal}</span> awaiting
                validation
              </>
            )}
            .
          </p>
          <ul className="mt-2 space-y-1">
            {actionable.slice(0, 4).map((r) => (
              <li
                key={`${r.payout_type}-${r.period}`}
                className="flex items-center justify-between gap-2 text-xs"
              >
                <span className="truncate text-tcs-text-secondary">
                  {formatPeriod(r.period)} · {payoutTypeShort(r.payout_type)}
                </span>
                <span className="shrink-0 rounded-full bg-st-warn-bg px-1.5 py-0.5 font-semibold text-st-warn-fg">
                  {r.queue_status === 'not_generated'
                    ? 'generate'
                    : r.queue_status === 'needs_validation'
                      ? `${r.pending_review_count} to verify`
                      : 'route to finance'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
